import { BackupTable, Close, Delete, FormatAlignLeft } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Theme,
} from "@mui/material";
import { PageLabel, PageLabelRule } from "generated/graphql";
import { FC, useMemo, useState } from "react";
import theme, { LinkSX } from "theme";
import { P } from "theme/typography";
import PageLabelRuleDialog from "./PageLabelRuleDialog";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";

interface PageLabelProps {
  label: PageLabel;
  rules: PageLabelRule[];
  onDelete: () => void;
  sx?: SxProps<Theme>;
}

const PageLabelDisplay: FC<PageLabelProps> = ({ label, rules, onDelete, sx }) => {
  const [showNewRule, setShowNewRule] = useState<boolean>(false);
  const [ruleForEdit, setRuleForEdit] = useState<PageLabelRule | undefined>(undefined);
  const groupedRules = useMemo(
    () =>
      rules.reduce<Record<string, PageLabelRule[]>>(
        (prev, rule) => {
          if (!rule.exclude) {
            if (!rule.groupName) {
              prev["No Group"]?.push(rule);
            } else if (prev[rule.groupName]) {
              prev[rule.groupName]?.push(rule);
            } else {
              prev[rule.groupName] = [rule];
            }
          }
          return prev;
        },
        { "No Group": [] }
      ),
    [rules]
  );
  const filteredGroupedRuleKeys = useMemo(
    () =>
      Object.entries(groupedRules)
        .filter(([, rules]) => rules.length)
        .map(([groupName]) => groupName),
    [groupedRules]
  );
  const rulesForExclusion = useMemo(() => rules.filter((r) => r.exclude), [rules]);

  const editRule = (rule: PageLabelRule) => {
    setRuleForEdit(rule);
  };

  return (
    <>
      {showNewRule && (
        <PageLabelRuleDialog
          open={showNewRule}
          close={() => setShowNewRule(false)}
          pageLabelId={label.id}
          pageLabelRules={rules}
        />
      )}
      {ruleForEdit && (
        <PageLabelRuleDialog
          open={!!ruleForEdit}
          close={() => setRuleForEdit(undefined)}
          pageLabelId={label.id}
          pageLabelRule={ruleForEdit}
          pageLabelRules={rules}
        />
      )}
      <Card variant="outlined" sx={{ display: "flex", flexDirection: "column", ...sx }}>
        <CardContent sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
            <Box
              sx={{
                borderBottom: `1px solid ${theme.palette.grey[100]}`,
                display: "flex",
                alignItems: "center",
              }}>
              {label.preventsLensHits ? (
                <>
                  <Close />
                  <P variant="body4" sx={{ color: "grey.400" }}>
                    Lens Hits are prevented
                  </P>
                </>
              ) : (
                <P variant="body4" sx={{ color: "grey.400" }}>
                  Lens Hits are not prevented
                </P>
              )}
            </Box>
            {label.triggersFormExtraction && (
              <Tooltip title="Triggers Form Extraction">
                <FormatAlignLeft />
              </Tooltip>
            )}
            {label.triggersTableExtraction && (
              <Tooltip title="Triggers Table Extraction">
                <BackupTable />
              </Tooltip>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <IconButton size="small" color="warning" onClick={onDelete}>
              <Delete />
            </IconButton>
          </Box>
          <Box sx={{ overflowY: "auto" }}>
            {rulesForExclusion.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <P variant="smSemiBold" sx={{ mr: 1, color: theme.palette.tertiary.dark }}>
                    Rules For Exclusion
                  </P>
                  <Tooltip arrow title="If any of these rules match, the label will not be applied">
                    <DoDisturbIcon />
                  </Tooltip>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Query Text</TableCell>
                      <TableCell width="75px">Fuzziness</TableCell>
                      <TableCell width="50px">Slop</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rulesForExclusion.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell onClick={() => editRule(rule)} sx={LinkSX}>
                          <P variant="block12">{rule.queryText}</P>
                        </TableCell>
                        <TableCell>{rule.queryFuzziness}</TableCell>
                        <TableCell>{rule.queryPhraseSlop}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {filteredGroupedRuleKeys.map((key) => (
              <Box key={key} sx={{ display: "flex", flexDirection: "column", mb: 3 }}>
                <P variant="smSemiBold" sx={{ mb: 1, color: theme.palette.grey["400"] }}>
                  Group: <b>{key}</b>
                </P>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Query Text</TableCell>
                      <TableCell width="75px">Fuzziness</TableCell>
                      <TableCell width="50px">Slop</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedRules[key]?.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell onClick={() => editRule(rule)} sx={LinkSX}>
                          <P variant="block12">{rule.queryText}</P>
                        </TableCell>
                        <TableCell>{rule.queryFuzziness}</TableCell>
                        <TableCell>{rule.queryPhraseSlop}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ))}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box>
            <Button color="primary" variant="contained" onClick={() => setShowNewRule(true)}>
              Add Rule
            </Button>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default PageLabelDisplay;
